import { useQuery } from '@tanstack/react-query';
import { Badge, cn } from '@wl/ui';
import { type ReactNode, useState } from 'react';
import { api } from '../lib/api';
import { ACCOUNT_STATUS, marketStatusText, ORDER_STATUS, SESSION_STATUS, SIDE_LABEL } from './invest-format';
import type { AccountStatus, FeedStatus, MarketStatus, OrderStatus, SessionStatus, Side, StreamState } from './invest-types';

export function OrderStatusBadge({ status }: { status: OrderStatus }) {
  const s = ORDER_STATUS[status];
  return <Badge tone={s.tone}>{s.label}</Badge>;
}

export function SessionBadge({ status }: { status: SessionStatus }) {
  const s = SESSION_STATUS[status];
  return <Badge tone={s.tone}>{s.label}</Badge>;
}

export function AccountStatusBadge({ status }: { status: AccountStatus }) {
  const s = ACCOUNT_STATUS[status];
  return <Badge tone={s.tone}>{s.label}</Badge>;
}

export function SideBadge({ side }: { side: Side }) {
  return <Badge tone={side === 'buy' ? 'success' : 'danger'}>{SIDE_LABEL[side]}</Badge>;
}

/** Canlı akış bağlantısı göstergesi. */
export function LiveIndicator({ state, feed }: { state: StreamState; feed: FeedStatus | null }) {
  const live = state === 'open' && feed === 'live';
  const label = state !== 'open' ? (state === 'connecting' ? 'Bağlanıyor' : 'Bağlantı koptu') : feed === 'unavailable' ? 'Fiyat servisi yok' : 'Canlı';
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-muted">
      <span className={cn('size-2 rounded-full', live ? 'animate-pulse bg-success' : state === 'connecting' ? 'bg-warning' : 'bg-danger')} />
      {label}
    </span>
  );
}

/**
 * Değer değişince arka planı kısa süre yeşil/kırmızı yanar. Önceki değer state'te
 * tutulur; değişimde key artar ve animasyon yeniden başlar.
 */
export function FlashValue({ value, children, className }: { value: number | null | undefined; children: ReactNode; className?: string }) {
  const [state, setState] = useState<{ value: number | null | undefined; dir: 'up' | 'down' | null; n: number }>({ value, dir: null, n: 0 });
  if (value !== state.value) {
    const dir = value !== null && value !== undefined && state.value !== null && state.value !== undefined ? (value > state.value ? 'up' : 'down') : null;
    setState({ value, dir, n: state.n + 1 });
  }
  return (
    <span key={state.n} className={cn('-mx-1 inline-block rounded px-1 tabular-nums', state.dir === 'up' && 'ui-flash-up', state.dir === 'down' && 'ui-flash-down', className)}>
      {children}
    </span>
  );
}

export function useMarketStatus() {
  return useQuery({
    queryKey: ['market', 'status'],
    queryFn: () => api.get<MarketStatus>('/market/status'),
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
}

/** Header altındaki ince seans şeridi. */
export function MarketStrip() {
  const q = useMarketStatus();
  if (!q.data) return null;
  const s = q.data;
  return (
    <div
      role="status"
      className={cn(
        'flex h-7 items-center gap-2 border-t px-3 text-xs font-medium md:px-6',
        s.open ? 'border-success/20 bg-success/10 text-success' : 'border-warning/20 bg-warning/10 text-warning',
      )}
    >
      <span className={cn('size-1.5 shrink-0 rounded-full', s.open ? 'animate-pulse bg-success' : 'bg-warning')} />
      <span className="truncate">{marketStatusText(s)}</span>
      <span className="ml-auto hidden text-muted sm:inline">BIST</span>
    </div>
  );
}
